import React, { useState } from "react";
import { ChevronDown, User, Building, PlusCircle } from "lucide-react";

const EditableField = ({
  label,
  value,
  name,
  onChange,
  isEditing,
  type = "text",
  placeholder = "",
}) => (
  <div className="flex justify-between items-center border-b border-slate-100 pb-2 mt-1.5">
    <span className="text-slate-500 w-1/3 text-[11px] font-medium">
      {label}
    </span>
    <div className="w-2/3 flex justify-end text-right">
      {isEditing ? (
        <input
          type={type}
          name={name}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          className="border border-slate-200 rounded-md px-2 py-1 text-[11px] w-full max-w-[200px] text-right bg-slate-50 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500 transition-all shadow-sm"
        />
      ) : (
        <span className="font-semibold text-[11px] text-slate-800">
          {value || "N/A"}
        </span>
      )}
    </div>
  </div>
);

export default function EmployeeDetailsGrid({
  editForm,
  isEditing,
  onChangeMain,
  onChangeNested,
  onChangeLanguage,
  onAddNested,
}) {
  // Use array to allow multiple sections to be open. Default to Personal and Org.
  const [openSections, setOpenSections] = useState(["Personal", "Org"]);

  const toggleSection = (sectionName) => {
    setOpenSections((prev) =>
      prev.includes(sectionName)
        ? prev.filter((s) => s !== sectionName)
        : [...prev, sectionName],
    );
  };

  const isOpen = (section) => openSections.includes(section);

  const getAccordionClass = (section) =>
    `border rounded-xl overflow-hidden transition-all duration-300 shadow-sm ${
      isEditing
        ? "border-blue-200 bg-blue-50/20"
        : "border-slate-100 bg-white hover:border-slate-300 hover:shadow-md"
    }`;

  const getButtonClass = (section) =>
    `w-full flex items-center justify-between p-4 bg-transparent outline-none cursor-pointer transition-colors ${
      isOpen(section) ? "bg-slate-50/50" : ""
    }`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start mt-1">
      {/* ---------------- LEFT COLUMN (Basic Info) ---------------- */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
          <User className="w-3.5 h-3.5 text-blue-600" />
          <h3 className="text-xs font-bold text-slate-900 tracking-tight">
            Basic Information
          </h3>
        </div>

        {/* Personal Info */}
        <div className={getAccordionClass("Personal")}>
          <button
            onClick={() => toggleSection("Personal")}
            className={getButtonClass("Personal")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Personal Details
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Personal") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Personal") && (
            <div className="p-4 pt-0 text-slate-700">
              <EditableField
                label="First Name"
                name="first_name"
                value={editForm.employee.first_name}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Middle Name"
                name="middle_name"
                value={editForm.employee.middle_name}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Last Name"
                name="last_name"
                value={editForm.employee.last_name}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Preferred Name"
                name="preferred_name"
                value={editForm.employee.preferred_name}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Gender"
                name="gender"
                value={editForm.employee.gender}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={editForm.employee.date_of_birth}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Blood Group"
                name="blood_group"
                value={editForm.employee.blood_group}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Marital Status"
                name="marital_status"
                value={editForm.employee.marital_status}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Nationality"
                name="nationality"
                value={editForm.employee.nationality}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
            </div>
          )}
        </div>

        {/* Contacts */}
        <div className={getAccordionClass("Contacts")}>
          <button
            onClick={() => toggleSection("Contacts")}
            className={getButtonClass("Contacts")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Contacts
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Contacts") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Contacts") && (
            <div className="p-4 pt-0 text-slate-700">
              <EditableField
                label="Personal Email"
                name="personal_email"
                type="email"
                value={editForm.employee.personal_email}
                onChange={onChangeMain}
                isEditing={isEditing}
              />
              <EditableField
                label="Secondary Phone"
                name="secondary_phone"
                value={editForm.employee.secondary_phone}
                onChange={onChangeMain}
                isEditing={isEditing}
              />

              {editForm.contacts.map((contact, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3 rounded-xl border border-slate-200 mt-3 shadow-sm relative"
                >
                  <div className="absolute -top-2 left-3 bg-white px-1 text-[9px] font-bold text-blue-600 uppercase tracking-wider">
                    Contact {idx + 1}
                  </div>
                  <EditableField
                    label="Type"
                    name="contact_type"
                    value={contact.contact_type}
                    onChange={(e) => onChangeNested(e, "contacts", idx)}
                    isEditing={isEditing}
                    placeholder="Emergency"
                  />
                  <EditableField
                    label="Full Name"
                    name="full_name"
                    value={contact.full_name}
                    onChange={(e) => onChangeNested(e, "contacts", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Relationship"
                    name="relationship"
                    value={contact.relationship}
                    onChange={(e) => onChangeNested(e, "contacts", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Phone"
                    name="phone"
                    value={contact.phone}
                    onChange={(e) => onChangeNested(e, "contacts", idx)}
                    isEditing={isEditing}
                  />
                </div>
              ))}
              {isEditing && (
                <button
                  onClick={() =>
                    onAddNested("contacts", {
                      contact_type: "",
                      full_name: "",
                      relationship: "",
                      phone: "",
                    })
                  }
                  className="mt-3 w-full py-2 border-2 border-dashed border-blue-200 text-blue-600 rounded-lg text-[11px] font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center justify-center gap-1.5"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Emergency Contact
                </button>
              )}
            </div>
          )}
        </div>

        {/* Addresses */}
        <div className={getAccordionClass("Addresses")}>
          <button
            onClick={() => toggleSection("Addresses")}
            className={getButtonClass("Addresses")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Addresses
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Addresses") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Addresses") && (
            <div className="p-4 pt-0 text-slate-700">
              {!isEditing && editForm.addresses.length === 0 && (
                <p className="text-[11px] text-slate-400 text-center py-2">
                  No addresses found.
                </p>
              )}
              {editForm.addresses.map((addr, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3 rounded-xl border border-slate-200 mb-3 shadow-sm relative mt-2"
                >
                  <div className="absolute -top-2 left-3 bg-white px-1 text-[9px] font-bold text-blue-600 uppercase tracking-wider">
                    Address {idx + 1}
                  </div>
                  <EditableField
                    label="Type"
                    name="address_type"
                    value={addr.address_type}
                    onChange={(e) => onChangeNested(e, "addresses", idx)}
                    isEditing={isEditing}
                    placeholder="Current/Permanent"
                  />
                  <EditableField
                    label="Line 1"
                    name="line1"
                    value={addr.line1}
                    onChange={(e) => onChangeNested(e, "addresses", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="City"
                    name="city"
                    value={addr.city}
                    onChange={(e) => onChangeNested(e, "addresses", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="State"
                    name="state"
                    value={addr.state}
                    onChange={(e) => onChangeNested(e, "addresses", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Country"
                    name="country"
                    value={addr.country}
                    onChange={(e) => onChangeNested(e, "addresses", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Postal Code"
                    name="postal_code"
                    value={addr.postal_code}
                    onChange={(e) => onChangeNested(e, "addresses", idx)}
                    isEditing={isEditing}
                  />
                </div>
              ))}
              {isEditing && (
                <button
                  onClick={() =>
                    onAddNested("addresses", {
                      address_type: "",
                      line1: "",
                      city: "",
                      state: "",
                      country: "",
                      postal_code: "",
                    })
                  }
                  className="w-full py-2 border-2 border-dashed border-blue-200 text-blue-600 rounded-lg text-[11px] font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center justify-center gap-1.5 mt-1"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Address
                </button>
              )}
            </div>
          )}
        </div>

        {/* Languages */}
        <div className={getAccordionClass("Languages")}>
          <button
            onClick={() => toggleSection("Languages")}
            className={getButtonClass("Languages")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Languages Known
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Languages") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Languages") && (
            <div className="p-4 pt-0 text-slate-700">
              {isEditing ? (
                <div className="flex flex-col space-y-1.5">
                  <span className="text-[9px] text-slate-500 font-medium">
                    Enter languages separated by commas (e.g., English, Hindi)
                  </span>
                  <input
                    type="text"
                    value={editForm.employee.languages_known?.join(", ") || ""}
                    onChange={onChangeLanguage}
                    className="border border-slate-200 rounded-md px-2 py-1.5 text-[11px] w-full bg-slate-50 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {editForm.employee.languages_known?.length > 0 ? (
                    editForm.employee.languages_known.map((lang, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md border border-slate-200 font-semibold text-[10px] shadow-sm"
                      >
                        {lang}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 text-[11px]">
                      No languages recorded.
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ---------------- RIGHT COLUMN (Org & Records) ---------------- */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
          <Building className="w-3.5 h-3.5 text-blue-600" />
          <h3 className="text-xs font-bold text-slate-900 tracking-tight">
            Organization & Records
          </h3>
        </div>

        {/* Org Assignments */}
        <div className={getAccordionClass("Org")}>
          <button
            onClick={() => toggleSection("Org")}
            className={getButtonClass("Org")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Assignments
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Org") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Org") && (
            <div className="p-4 pt-0 text-slate-700">
              {!isEditing && editForm.orgAssignments.length === 0 && (
                <p className="text-[11px] text-slate-400 text-center py-2">
                  No assignments found.
                </p>
              )}
              {editForm.orgAssignments.map((org, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3 rounded-xl border border-slate-200 mb-3 shadow-sm relative mt-2"
                >
                  <div className="absolute -top-2 left-3 bg-white px-1 text-[9px] font-bold text-blue-600 uppercase tracking-wider">
                    Role {idx + 1}
                  </div>
                  <EditableField
                    label="Department"
                    name="department"
                    value={org.department}
                    onChange={(e) => onChangeNested(e, "orgAssignments", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Designation"
                    name="designation"
                    value={org.designation}
                    onChange={(e) => onChangeNested(e, "orgAssignments", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Start Date"
                    name="start_date"
                    type="date"
                    value={org.start_date}
                    onChange={(e) => onChangeNested(e, "orgAssignments", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={org.end_date}
                    onChange={(e) => onChangeNested(e, "orgAssignments", idx)}
                    isEditing={isEditing}
                  />
                </div>
              ))}
              {isEditing && (
                <button
                  onClick={() =>
                    onAddNested("orgAssignments", {
                      department: "",
                      designation: "",
                      start_date: "",
                      end_date: "",
                    })
                  }
                  className="w-full py-2 border-2 border-dashed border-blue-200 text-blue-600 rounded-lg text-[11px] font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center justify-center gap-1.5 mt-1"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Assignment
                </button>
              )}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className={getAccordionClass("Docs")}>
          <button
            onClick={() => toggleSection("Docs")}
            className={getButtonClass("Docs")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Documents
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Docs") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Docs") && (
            <div className="p-4 pt-0 text-slate-700">
              {!isEditing && editForm.documents.length === 0 && (
                <p className="text-[11px] text-slate-400 text-center py-2">
                  No documents found.
                </p>
              )}
              {editForm.documents.map((doc, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3 rounded-xl border border-slate-200 mb-3 shadow-sm relative mt-2"
                >
                  <EditableField
                    label="Doc Type"
                    name="document_type"
                    value={doc.document_type}
                    onChange={(e) => onChangeNested(e, "documents", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Number"
                    name="document_number"
                    value={doc.document_number}
                    onChange={(e) => onChangeNested(e, "documents", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Status"
                    name="verification_status"
                    value={doc.verification_status || doc.status}
                    onChange={(e) => onChangeNested(e, "documents", idx)}
                    isEditing={isEditing}
                  />
                </div>
              ))}
              {isEditing && (
                <button
                  onClick={() =>
                    onAddNested("documents", {
                      document_type: "",
                      document_number: "",
                      verification_status: "Pending",
                    })
                  }
                  className="w-full py-2 border-2 border-dashed border-blue-200 text-blue-600 rounded-lg text-[11px] font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center justify-center gap-1.5 mt-1"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Document
                </button>
              )}
            </div>
          )}
        </div>

        {/* Skills */}
        <div className={getAccordionClass("Skills")}>
          <button
            onClick={() => toggleSection("Skills")}
            className={getButtonClass("Skills")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Skills
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Skills") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Skills") && (
            <div className="p-4 pt-0 text-slate-700">
              {!isEditing && editForm.skills.length === 0 && (
                <p className="text-[11px] text-slate-400 text-center py-2">
                  No skills found.
                </p>
              )}
              {editForm.skills.map((skill, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3 rounded-xl border border-slate-200 mb-3 shadow-sm relative mt-2"
                >
                  <EditableField
                    label="Skill"
                    name="name"
                    value={skill.name}
                    onChange={(e) => onChangeNested(e, "skills", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Level"
                    name="proficiency_level"
                    value={skill.proficiency_level}
                    onChange={(e) => onChangeNested(e, "skills", idx)}
                    isEditing={isEditing}
                  />
                </div>
              ))}
              {isEditing && (
                <button
                  onClick={() =>
                    onAddNested("skills", {
                      name: "",
                      proficiency_level: "Beginner",
                    })
                  }
                  className="w-full py-2 border-2 border-dashed border-blue-200 text-blue-600 rounded-lg text-[11px] font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center justify-center gap-1.5 mt-1"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Skill
                </button>
              )}
            </div>
          )}
        </div>

        {/* Certifications */}
        <div className={getAccordionClass("Certifications")}>
          <button
            onClick={() => toggleSection("Certifications")}
            className={getButtonClass("Certifications")}
          >
            <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
              Certifications
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("Certifications") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("Certifications") && (
            <div className="p-4 pt-0 text-slate-700">
              {!isEditing && editForm.certifications.length === 0 && (
                <p className="text-[11px] text-slate-400 text-center py-2">
                  No certifications found.
                </p>
              )}
              {editForm.certifications.map((cert, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3 rounded-xl border border-slate-200 mb-3 shadow-sm relative mt-2"
                >
                  <EditableField
                    label="Cert Name"
                    name="name"
                    value={cert.name}
                    onChange={(e) => onChangeNested(e, "certifications", idx)}
                    isEditing={isEditing}
                  />
                  <EditableField
                    label="Issuer"
                    name="issuer"
                    value={cert.issuer}
                    onChange={(e) => onChangeNested(e, "certifications", idx)}
                    isEditing={isEditing}
                  />
                </div>
              ))}
              {isEditing && (
                <button
                  onClick={() =>
                    onAddNested("certifications", { name: "", issuer: "" })
                  }
                  className="w-full py-2 border-2 border-dashed border-blue-200 text-blue-600 rounded-lg text-[11px] font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center justify-center gap-1.5 mt-1"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Certification
                </button>
              )}
            </div>
          )}
        </div>

        {/* System Info (Read Only) */}
        <div className="border border-slate-100 rounded-xl overflow-hidden transition-all duration-300 bg-slate-50/50 shadow-sm">
          <button
            onClick={() => toggleSection("System")}
            className={getButtonClass("System")}
          >
            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
              System Information
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen("System") ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen("System") && (
            <div className="p-4 pt-0 text-slate-700">
              <EditableField
                label="Created At"
                value={
                  editForm.employee.created_at
                    ? new Date(editForm.employee.created_at).toLocaleString()
                    : "N/A"
                }
                isEditing={false}
              />
              <EditableField
                label="Updated At"
                value={
                  editForm.employee.updated_at
                    ? new Date(editForm.employee.updated_at).toLocaleString()
                    : "N/A"
                }
                isEditing={false}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
